import { v4 as uuidv4 } from "uuid";
import { hasOwnProperty, isDefined } from "../../infra3d";
import { getData } from "../../infra3d/utils/ajax";
import { Obj } from "../internal";
import { FeatureStyle, Geometry, GEOMETRYTYPE } from "../featuretree/internal";
import { DEFAULTLAYERSTYLE, LAYERTYPE, FEATURELOADINGSTRATEGY, StrategyHandler, FeatureSelector, LayerEditor, LayerPropertyDef, SystempropertyHandler, } from "./internal";
export var LAYERMODE;
(function (LAYERMODE) {
    LAYERMODE[LAYERMODE["IDLE"] = 0] = "IDLE";
    LAYERMODE[LAYERMODE["EDIT"] = 1] = "EDIT";
})(LAYERMODE || (LAYERMODE = {}));
/**
 * Base-Class to store and manage Features in a GIS-like Layer.
 *
 * @class Layer
 */
export class Layer extends Obj {
    constructor(scene, opt_options) {
        super();
        /**
         * The Array of features that this Collection is managing.
         * @type {FeatureCollection}
         */
        this.featurecollection = null;
        /**
         * The strategy that should be used to call the loadFeatures-function.
         * @type {number}
         */
        this.strategy = FEATURELOADINGSTRATEGY.BBOX;
        /**
         * The strategy options. See strategies for details
         * @type {{}}
         */
        this.strategyoptions = {};
        /**
         * Feature-Load-State indicator. mainly used by the strategyhandler.
         * @type {boolean}
         */
        this.needsReload = true;
        /**
         * Indicates wether the features hosted by the layer should be selectable for tools and other functions that
         * uses picking.
         * @type {boolean}
         */
        this.selectable = true;
        /**
         * Indicates wether the features hosted by the layer should be editable.
         * @type {boolean}
         */
        this.editable = true;
        /**
         * Visibility of the features hosted by the layer.
         * @type {boolean}
         */
        this.visible = true;
        /**
         * Geometry-Type of the features. It is recommended to define for all features in the layer the same
         * geometry-type here.
         * @type {GEOMETRYTYPE}
         */
        this.geometrytype = GEOMETRYTYPE.UNDEFINED;
        /**
         * Property-Definitions.
         * @type {Array}
         */
        this.propertydef = [];
        /**
         * Tooltip information
         * @type {Array}
         */
        this.tooltip = [];
        /**
         * Layertype. Value set by extensions of this base-class
         * @type {LAYERTYPE}
         */
        this.type = LAYERTYPE.BASE;
        /**
         * Layermode. Used to switch between Edit- and Idle-Mode.
         * @type {LAYERMODE}
         */
        this.mode = LAYERMODE.IDLE;
        /**
         *  LayerEditor-Object. Used in Editmode to draw, update and delete features.
         * @type {LayerEditor}
         */
        this.layereditor = null;
        /**
         * FeatureSelector-Object. Used to manage, which features are
         * selected and which are not.
         * @type {FeatureSelector}
         * @public
         */
        this.featureselector = null;
        this.systempropertyhandler = null;
        /**
         * Feature-Index for fast accessing by featureid.
         * @type {{}}
         * @private
         */
        this._featureidx = {};
        /**
         * Called on any changes on the layer.
         * @type {any}
         */
        this.onlayerupdated = null;
        /**
         * Called on any saves on the layer.
         * @type {any}
         */
        this.onlayersaved = null;
        /**
         * The name of the topic the layer belongs to.
         * @type {string}
         */
        this.topic = "";
        /**
         * Topic expansion for visualization in a layer-tree.
         * @type {string}
         */
        this.expanded = true;
        this.coordsdim = 3;
        this.filters = [];
        this.attributemask = { type: "standard" };
        this.isframefeaturelayer = false;
        /**
         * Stores any options passed on construct
         * @type {{}}
         */
        this.options = {};
        this.addedFromUser = false;
        this.scene = scene;
        const options = opt_options || {};
        this.read = options.read || "";
        this.loadFeatures = options.loadFeatures || this._loadFeatures_internal;
        this.exportFeatures = options.exportFeatures || this._exportFeatures_internal;
        this.saveFeatures = options.saveFeatures || this._saveFeatures_internal;
        if (options.strategy)
            options.strategy = StrategyHandler.strategyToNumber(options.strategy);
        this.strategy = options.strategy || FEATURELOADINGSTRATEGY.BBOX;
        this.strategyoptions = options.strategyoptions || {};
        if (typeof options.style == "object" && !(options.style instanceof FeatureStyle)) {
            options.style = new FeatureStyle(options.style);
        }
        this.geometrytype = Geometry.toNumber(options.geometrytype);
        this.style = options.style || DEFAULTLAYERSTYLE[this.geometrytype];
        if (typeof options.highlightstyle == "object" && !(options.highlightstyle instanceof FeatureStyle)) {
            options.highlightstyle = new FeatureStyle(options.highlightstyle);
        }
        this.highlightstyle = options.highlightstyle || DEFAULTLAYERSTYLE.HIGHLIGHTSTYLE;
        this.layername = isDefined(options.layername)
            ? options.layername
            : (typeof this.read === "string" && this.read) != ""
                ? this.read.split("/")[this.read.split("/").length - 1]
                : isDefined(options.title)
                    ? options.title.toLowerCase().replace(" ", "-")
                    : "layer1";
        this.title = options.title || this.layername;
        this.id = options.id || uuidv4();
        this.selectable = typeof options.selectable === "undefined" ? true : options.selectable;
        this.editable = typeof options.editable === "undefined" ? true : options.editable;
        this.visible = typeof options.visible === "undefined" ? true : options.visible;
        if (options.propertydef) {
            const propertydef_parsed = [];
            for (let i = 0; i < options.propertydef.length; i++) {
                if (typeof options.propertydef[i] == "object" && !(options.propertydef[i] instanceof LayerPropertyDef))
                    options.propertydef[i] = LayerPropertyDef.propertydefFromLayerdefinition(options.propertydef[i]);
                propertydef_parsed.push(options.propertydef[i]);
            }
            options.propertydef = propertydef_parsed;
        }
        this.propertydef = options.propertydef || [];
        this.tooltip = options.tooltip || [];
        this.topic = options.topic || "";
        this.expanded = typeof options.expanded === "undefined" ? true : options.expanded;
        this.coordsdim = options.coordsdim || 3;
        //Init FeatureCollection
        const featurehandler = this.scene.getFeatureHandler();
        this.isframefeaturelayer =
            typeof options.isframefeaturelayer === "undefined" ? false : options.isframefeaturelayer;
        this.featurecollection = featurehandler.createFeatureCollection(this.style, this.highlightstyle, this.layername, this.selectable, this.isframefeaturelayer);
        this.setVisible(this.visible);
        //Init LayerEditor
        this.layereditor = new LayerEditor(this.scene, this);
        //Init SystempropertyHandler
        this.systempropertyhandler = new SystempropertyHandler(this.scene, this, options.systemproperties || {});
        //Init Featureselector
        this.featureselector = new FeatureSelector(this.scene, this);
        this.filters = options.filters || [];
        this.attributemask = options.attributemask || { type: "standard" };
        this.options = options;
        if (this.options.addedFromUser) {
            this.addedFromUser = this.options.addedFromUser;
        }
    }
    /**
     * Basic-Implementation of a feature-load-function. Usually this function is overwritten in extensions or by
     * options.
     * @param extent Map-Extent depending on the strategy
     * @param srid Spatial reference id of the map extent
     * @private
     */
    _loadFeatures_internal(extent, srid) {
        const url = typeof this.read === "function"
            ? this.read(extent, srid)
            : this.read;
        const handleSuccess = (geoframe) => {
            this.addFeatures(geoframe);
        };
        const handleError = function (error) {
            console.error(error);
        };
        getData(url, handleSuccess, handleError);
    }
    /**
     * Adds all Features in a FeatureCollection to the layer.
     * todo: check srid
     * @param geojson type FeatureCollection
     * @param append True to append the features.
     * @param suppressevent True to suppress calling Layerupdated-events.
     */
    addFeatures(geojson, append = false, suppressevent = false) {
        if (geojson.type == "FeatureCollection") {
            const added = [], updated = [], deleted = [];
            const featureindex = {};
            for (let j = 0; j < geojson.features.length; j++) {
                let raw_feature = geojson.features[j];
                //Initialize geometry-type from the first feature if not yet set
                if (this.geometrytype == GEOMETRYTYPE.UNDEFINED)
                    this.geometrytype = Geometry.toNumber(raw_feature.geometry.type);
                if (raw_feature.geometry) {
                    const geometrytype = Geometry.toNumber(raw_feature.geometry.type);
                    if (!(geometrytype == GEOMETRYTYPE.MULTIPOLYGON && this.geometrytype == GEOMETRYTYPE.POLYGON) &&
                        !(geometrytype == GEOMETRYTYPE.MULTILINESTRING && this.geometrytype == GEOMETRYTYPE.LINESTRING) &&
                        !(geometrytype == this.geometrytype)) {
                        console.log("Error: Failed to load features on layer " + this.layername + ". Geometrytype mismatch.");
                        return;
                    }
                }
                this.getSystempropertyHandler().handleSystemProperties(raw_feature, "onload");
                //replace the raw-feature with the edited one if editmode is on. otherwise the editions will get lost
                // while adding the same-features on reload.
                if (this.mode == LAYERMODE.EDIT) {
                    const update_feature = this.getLayerEditor().featuresupdate[raw_feature.id.toString()];
                    if (update_feature) {
                        raw_feature = JSON.parse(JSON.stringify(update_feature));
                    }
                }
                let mm_feature = this._featureidx[raw_feature.id.toString()];
                if (!mm_feature) {
                    mm_feature = this.featurecollection.addFeatureFromGeoJSON(raw_feature);
                    added.push(raw_feature);
                }
                else {
                    const highlight = mm_feature.highlighton;
                    mm_feature.destroy();
                    mm_feature.parseFromJSON(raw_feature);
                    if (highlight)
                        mm_feature.highlightOn();
                    updated.push(raw_feature);
                }
                if (this.featureselector.isInSelection(mm_feature.id)) {
                    mm_feature.highlightOn();
                    mm_feature.select();
                }
                featureindex[mm_feature.id.toString()] = mm_feature;
            }
            if (!append) {
                for (const id in this._featureidx) {
                    if (!hasOwnProperty(featureindex, id)) {
                        this.featurecollection.destroyFeature(this._featureidx[id]);
                        deleted.push(id);
                    }
                }
                this._featureidx = featureindex;
            }
            else {
                this._featureidx = { ...this._featureidx, ...featureindex };
            }
            this.setVisible(this.visible);
            if (this.onlayerupdated && !suppressevent) {
                this.onlayerupdated(this, {
                    added: added,
                    updated: updated,
                    removed: deleted,
                });
            }
        }
    }
    /**
     * Removes features with the given id.
     * @param ids Array of GeoJSON's feature id's.
     * @param suppressevent True to suppress calling Layerupdated-events.
     */
    removeFeatures(ids, suppressevent = false) {
        for (let i = 0; i < ids.length; i++) {
            this.featurecollection.destroyFeature(this.getFeatureById(ids[i]));
            delete this._featureidx[ids[i]];
        }
        this.featurecollection.update();
        if (this.onlayerupdated && ids.length > 0 && !suppressevent)
            this.onlayerupdated(this, {
                removed: ids,
            });
    }
    /**
     * shows features with the given id, hides others
     * @param ids
     */
    setLocalFiltersByIds(ids) {
        for (let i = 0; i < this.featurecollection.features.length; i++) {
            if (ids.indexOf(this.featurecollection.features[i].id) < 0) {
                this.featurecollection.features[i].hide();
            }
            else {
                this.featurecollection.features[i].show();
            }
        }
        this.featurecollection.update();
    }
    /**
     * filters features visibly (still appear in table but not in scene) according to passed filters
     * @param filters
     */
    setLocalFilters(filters) {
        for (let i = 0; i < this.featurecollection.features.length; i++) {
            const props = this.featurecollection.features[i].properties;
            let show = true;
            for (let j = 0; j < filters.length; j++) {
                if (props[filters[j].property] == filters[j].value) {
                    show = show && true;
                }
                else {
                    show = show && false;
                }
            }
            if (show) {
                this.featurecollection.features[i].show();
            }
            else {
                this.featurecollection.features[i].hide();
            }
        }
        this.featurecollection.update();
    }
    /**
     * returns a feature as Feature by id
     * @param id GeoJSON's feature id.
     * @returns {null}
     */
    getFeatureById(id) {
        const feature = this._featureidx[id.toString()];
        return feature !== undefined ? feature : null;
    }
    getFeatureCollection() {
        return this.featurecollection;
    }
    /**
     * Collects the edited features from the LayerEditor and calls the saveFeatures-function. Function can only be used if the edit mode is on.
     * @param cbs The function is called if the savings had been successful. It returns the saved features as GeoJSON-FeatureCollection (also the deletions).
     * @param cbf The function is called if the saving had been failed.
     */
    save(cbs, cbf) {
        if (this.mode == LAYERMODE.EDIT) {
            //Insert
            const featureinsert = this.getLayerEditor().featuresinsert;
            const inserts = JSON.parse(featureinsert.toGeoJsonString()); //the crs-property is set here to the
            // epsg-code of the current stream.
            //Update
            const features_to_update = [];
            const featuresupdate = this.getLayerEditor().featuresupdate;
            for (const key in featuresupdate) {
                features_to_update.push(featuresupdate[key]);
            }
            const updates = {
                type: "FeatureCollection",
                features: features_to_update,
                crs: inserts.crs,
            };
            if (this.coordsdim == 2) {
                Layer.geojsonRemoveZValues(inserts);
                Layer.geojsonRemoveZValues(updates);
            }
            //Delete
            //put feature-ids in a featurecollection
            const featuresdelete = this.getLayerEditor().featuresdelete;
            const features_to_delete = [];
            for (let i = 0; i < featuresdelete.length; i++) {
                features_to_delete.push({
                    type: "Feature",
                    id: featuresdelete[i],
                    geometry: null,
                });
            }
            const deletions = {
                type: "FeatureCollection",
                features: features_to_delete,
                crs: inserts.crs,
            };
            const saveresults = {
                inserts: {
                    success: inserts.features.length == 0,
                    geojson: inserts.features.length == 0 ? inserts : null,
                },
                updates: {
                    success: updates.features.length == 0,
                    geojson: updates.features.length == 0 ? updates : null,
                },
                deletions: {
                    success: deletions.features.length == 0,
                    geojson: deletions.features.length == 0 ? deletions : null,
                },
            };
            this.saveFeatures(inserts, updates, deletions, (res_inserts, res_updates, res_deletions) => {
                if (res_inserts) {
                    if (res_inserts.features.length == inserts.features.length) {
                        saveresults.inserts.success = true;
                        saveresults.inserts.geojson = res_inserts;
                        this.addFeatures(res_inserts, true);
                    }
                }
                if (res_updates) {
                    if (res_updates.features.length == updates.features.length) {
                        saveresults.updates.success = true;
                        saveresults.updates.geojson = res_updates;
                        this.addFeatures(res_updates, true);
                    }
                }
                if (res_deletions) {
                    if (res_deletions.features.length == deletions.features.length) {
                        saveresults.deletions.success = true;
                        saveresults.deletions.geojson = res_deletions;
                        //Deletions
                        const deleted_ids = [];
                        for (const i in res_deletions.features) {
                            if (hasOwnProperty(res_deletions.features, i)) {
                                deleted_ids.push(deletions.features[i].id);
                            }
                        }
                        this.removeFeatures(deleted_ids, true);
                    }
                }
                if (saveresults.inserts.success && saveresults.updates.success && saveresults.deletions.success) {
                    this.featurecollection.update();
                    this.reset();
                    if (cbs) {
                        cbs.call(this, saveresults.inserts.geojson, saveresults.updates.geojson, saveresults.deletions.geojson);
                    }
                    if (this.onlayersaved) {
                        this.onlayersaved(this, saveresults.inserts.geojson, saveresults.updates.geojson, saveresults.deletions.geojson);
                    }
                }
            }, (message) => {
                if (cbf)
                    cbf.call(this, message);
            });
        }
        else {
            cbf.call(this, "Unable to save. The layer is not in edit-mode.");
        }
    }
    _saveFeatures_internal(inserts, updates, deletions, cbs, cbf) {
        this.reset();
    }
    /**
     * Resets the changes made by the LayerEditor if the edit mode is on. Calls the reload-function.
     */
    reset() {
        if (this.mode == LAYERMODE.EDIT)
            this.getLayerEditor().reset();
    }
    /**
     * Reloads the layer. Sets the Layer's needsReload=true and calls the LAYERUPDATED-Event. This forces the strategy-handler to call the loadFeatures-function.
     */
    reload() {
        this.needsReload = true;
        if (this.onlayerupdated) {
            this.onlayerupdated(this, {
                reload: true,
            });
        }
    }
    /**
     * Destroys the Layer. Resets the LayerEditor and destroys the FeatureCollection.
     */
    destroy() {
        this.layereditor.reset();
        this.featurecollection.destroy();
        this.featurecollection.features = [];
        this.featurecollection.update();
    }
    /**
     * Set the visibility of the layer
     * @param visibility
     */
    setVisible(visibility) {
        if (visibility) {
            this.featurecollection.show();
            //hide deletions if edit-mode is on
            if (this.mode == LAYERMODE.EDIT) {
                for (let i = 0; i < this.getLayerEditor().featuresdelete.length; i++) {
                    const mm_feature = this.getFeatureById(this.getLayerEditor().featuresdelete[i].toString());
                    if (mm_feature) {
                        mm_feature.hide();
                        mm_feature.update(true);
                    }
                }
            }
        }
        else {
            this.featurecollection.hide();
        }
        if (this.visible != visibility) {
            this.visible = visibility;
            if (this.onlayerupdated) {
                this.onlayerupdated(this, {
                    visibility: this.visible,
                });
            }
        }
    }
    /**
     * Gets the visibility of the layer.
     *
     * @returns {boolean}
     */
    getVisible() {
        return this.visible;
    }
    /**
     * Set the layer to edit mode
     * @param setmode
     */
    setEditMode(setmode) {
        if (setmode && this.editable) {
            this.mode = LAYERMODE.EDIT;
        }
        else {
            this.mode = LAYERMODE.IDLE;
        }
        if (this.onlayerupdated) {
            this.onlayerupdated(this, {
                mode: this.mode,
            });
        }
        return this.mode;
    }
    /**
     * Sets the snapability of the layer on the ToolHandler. The featurecollection hosted by the layer will then be snappable for all Tools created by the ToolHandler.
     * @param snapable
     */
    setSnapable(snapable) {
        const th = this.scene.getToolHandler();
        if (snapable) {
            th.addSnapLayer(this.featurecollection);
            if (this.getLayerEditor())
                th.addSnapLayer(this.getLayerEditor().featuresinsert);
        }
        else {
            th.removeSnapLayer(this.featurecollection);
            if (this.getLayerEditor())
                th.removeSnapLayer(this.getLayerEditor().featuresinsert);
        }
    }
    /**
     * Returns the default feature-properties. The default-properties can be defined by the layer propertydef-option (see options).
     * @returns {{}}
     */
    getDefaultProperties() {
        const props = {};
        for (let i = 0; i < this.propertydef.length; i++) {
            props[this.propertydef[i].key] = this.propertydef[i].defaultvalue;
        }
        return props;
    }
    /**
     * Returns LayerEditor object of the layer. Only available if layer-mode is edit-mode.
     * @returns {LayerEditor}
     */
    getLayerEditor() {
        return this.mode == LAYERMODE.EDIT ? this.layereditor : null;
    }
    /**
     * Returns SystempropertyHandler object of the layer.
     * @returns {SystempropertyHandler}
     */
    getSystempropertyHandler() {
        return this.systempropertyhandler;
    }
    /**
     * Returns the currently loaded GeoJSON-FeatureCollection. If given it uses the options "start" and "limit" to slice the feature-array. If given it uses the first element of the array "sorters" of the options to perform a local sorting of the feature-array. If the option "countonly" is given the length of the feature-array is retured.
     * @param cbs
     * @param cbf
     * @param options
     */
    _exportFeatures_internal(cbs, cbf, options) {
        const fc = JSON.parse(this.featurecollection.toGeoJsonString());
        const countonly = typeof options.countonly === "undefined" ? false : options.countonly;
        const sorters = typeof options.sorters === "undefined" ? false : options.sorters;
        const start = options.start || 0;
        const limit = options.limit || 10000;
        if (countonly) {
            cbs(fc.features.length);
        }
        else {
            if (sorters) {
                if (sorters.length > 0) {
                    Layer.localSort(fc, sorters[0]);
                }
            }
            fc.features = fc.features.slice(start, start + limit);
            cbs(fc);
        }
    }
    /**
     * Sets the OnLayerUpdated-Callback-Function
     * @method setOnLayerUpdated
     * @param {Function} f
     */
    setOnLayerUpdated(f) {
        this.onlayerupdated = f;
    }
    /**
     * Sets the OnLayerSaved-Callback-Function
     * @method setOnLayerSaved
     * @param {Function} f
     */
    setOnLayerSaved(f) {
        this.onlayersaved = f;
    }
    /**
     * Sets an option given its key and value
     * @param options
     */
    setOptions(options) {
        for (const key in options) {
            if (hasOwnProperty(options, key)) {
                let value = options[key];
                switch (key) {
                    case "style":
                        if (typeof value == "object" && !(value instanceof FeatureStyle))
                            value = new FeatureStyle(value);
                        if (value instanceof FeatureStyle) {
                            this.style = value;
                            this.featurecollection.changeStyle(value, this.highlightstyle);
                        }
                        else {
                            console.log("not a valid style set");
                        }
                        break;
                    case "title":
                        this.title = value;
                        break;
                    default:
                        console.warn("layer " + this.layername + ": the option " + key + " can not be changed.");
                        break;
                }
            }
        }
        if (this.onlayerupdated) {
            this.onlayerupdated(this, {
                options: options,
            });
        }
    }
    /**
     * Sorts a geojson FeatureCollection by defined properties and/or id
     * @param fc geojson FeatureCollection
     * @param sorter Object with keyes: 'property', 'direction' in one of [ASC,DESC].
     */
    static localSort(fc, sorter) {
        fc.features.sort(function (a, b) {
            let order = 0;
            if (sorter.property == "id") {
                if (parseFloat(a.id) > parseFloat(b.id)) {
                    order = 1;
                }
                else {
                    order = -1;
                }
            }
            else {
                if (Object.prototype.hasOwnProperty.call(a.properties, sorter.property)) {
                    //if field doesn't exist don't sort
                    if (a.properties[sorter.property] != null) {
                        if (a.properties[sorter.property] >= b.properties[sorter.property]) {
                            order = 1;
                        }
                        else {
                            order = -1;
                        }
                    }
                }
            }
            return sorter.direction == "ASC" ? order : -order;
        });
    }
    static geojsonRemoveZValues(geojson) {
        let features = [];
        if (geojson.type == "FeatureCollection") {
            features = geojson.features;
        }
        else if (geojson.type == "Feature") {
            features = [geojson];
        }
        for (let i = 0; i < features.length; i++) {
            const feature = features[i];
            switch (feature.geometry.type) {
                case "Point":
                    feature.geometry.coordinates.splice(2);
                    break;
                case "LineString":
                case "MultiPoint":
                    for (let j = 0; j < feature.geometry.coordinates.length; j++) {
                        feature.geometry.coordinates[j].splice(2);
                    }
                    break;
                case "Polygon":
                case "MultiLineString":
                    for (let m = 0; m < feature.geometry.coordinates.length; m++) {
                        for (let n = 0; n < feature.geometry.coordinates[m].length; n++) {
                            feature.geometry.coordinates[m][n].splice(2);
                        }
                    }
                    break;
                case "MultiPolygon":
                    for (let m = 0; m < feature.geometry.coordinates.length; m++) {
                        for (let n = 0; n < feature.geometry.coordinates[m].length; n++) {
                            for (let k = 0; k < feature.geometry.coordinates[m][n].length; k++) {
                                feature.geometry.coordinates[m][n][k].splice(2);
                            }
                        }
                    }
                    break;
            }
        }
    }
}
//# sourceMappingURL=layer.js.map